import { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { cpf, cnpj } from "cpf-cnpj-validator";
import { AiFillDelete } from "react-icons/ai";
import { MdArrowDropDown } from "react-icons/md";
import {
  DeleteFornecedorUnidades,
  PacthFornecedorEmbalagem,
  PostFornecedorEmbalagem,
  PostFornecedorUnidade,
} from "../../../data/services";

type Unidade = {
  idFornecedorUnidade?: number;
  fornecedorUnidadeDescricao: string;
  idFornecedor: number;
  fornecedorDescricao?: string;
  deleted?: number;
};

type TipoDeEmbalagem = {
  idTipoDeEmbalagem: number | string;
  tipoEmbalagem: string;
  deleted?: number;
};

type TipoEmbalagemOption = {
  idTipoEmbalagem: number | string;
  tipoEmbalagem: string;
  deleted?: number;
};

type FornecedorData = {
  idFornecedor: number;
  fornecedorDescricao: string;
  nomeFantasia?: string | null;
  cpfCnpj?: string | null;
  idUsuario?: number | null;
  deleted?: number;
  unidades: Unidade[];
  tipoDeEmbalagens: TipoDeEmbalagem[];
  embalagens?: any[]; // pode ignorar
};

type Props = {
  fornecedor: FornecedorData;
  tipoEmbalagensOptions: TipoEmbalagemOption[];
  onCancel: () => void;
};

const EditarFornecedor = ({
  fornecedor,
  tipoEmbalagensOptions,
  onCancel,
}: Props) => {
  // Estado inicial, agora usando tipoDeEmbalagens e unidades diretamente
  const [data, setData] = useState<FornecedorData>({
    ...fornecedor,
    unidades: fornecedor.unidades ?? [],
    tipoDeEmbalagens: fornecedor.tipoDeEmbalagens ?? [],
  });

  const [novaUnidade, setNovaUnidade] = useState("");
  const [showSelect, setShowSelect] = useState(false);
  const [loading, setLoading] = useState(false);

  const [tipoEmbalagensVinculadas, setTipoEmbalagensVinculadas] = useState<
    TipoDeEmbalagem[]
  >(data.tipoDeEmbalagens);

  // Atualiza opções para adicionar — remove as que já estão vinculadas
  const [tipoEmbalagensParaAdicionar, setTipoEmbalagensParaAdicionar] =
    useState<TipoEmbalagemOption[]>([]);

  useEffect(() => {
    const vinculadas = tipoEmbalagensVinculadas ?? [];
    setTipoEmbalagensParaAdicionar(
      tipoEmbalagensOptions.filter(
        (option) =>
          !vinculadas.some(
            (vinc) =>
              Number(vinc.idTipoDeEmbalagem) === Number(option.idTipoEmbalagem)
          )
      )
    );
  }, [tipoEmbalagensVinculadas, tipoEmbalagensOptions]);

  const handleAddUnidade = async () => {
    if (novaUnidade.trim().length === 0) return;

    const nova = {
      fornecedorUnidadeDescricao: novaUnidade.trim(),
      idFornecedor: data.idFornecedor,
    };

    try {
      const novaUnidadeSalva = await PostFornecedorUnidade(nova);

      setData((prev) => ({
        ...prev,
        unidades: [...prev.unidades, novaUnidadeSalva], // usa unidade com id
      }));

      setNovaUnidade("");
    } catch (err) {
      toast.error("Erro ao adicionar unidade.");
    }
  };

  const handleRemoverUnidade = async (unit: any) => {
    if (!unit?.idFornecedorUnidade) return;

    try {
      await DeleteFornecedorUnidades(unit.idFornecedorUnidade);

      setData((prev) => ({
        ...prev,
        unidades: prev.unidades.filter(
          (u) => u.idFornecedorUnidade !== unit.idFornecedorUnidade
        ),
      }));

      toast.success("Unidade removida com sucesso.");
    } catch (err) {
      toast.error("Erro ao remover unidade.");
    }
  };

  const handleToggleEmbalagem = (add: TipoEmbalagemOption) => {
    const novoTipoEmb: TipoDeEmbalagem = {
      idTipoDeEmbalagem: add.idTipoEmbalagem,
      tipoEmbalagem: add.tipoEmbalagem,
    };
    PostFornecedorEmbalagem({
      idTipoDeEmbalagem: add.idTipoEmbalagem,
      idFornecedor: data.idFornecedor,
    });

    setTipoEmbalagensVinculadas((prev) => [...prev, novoTipoEmb]);
    setShowSelect(false);
  };

  const handleRemoverEmbalagem = (idTipoEmbalagem: any) => {
    // adicionar função para remover embalagem daquele fornecedor
    PacthFornecedorEmbalagem(fornecedor.idFornecedor, idTipoEmbalagem); // testar
    setTipoEmbalagensVinculadas((prev) =>
      prev.filter(
        (e) => Number(e.idTipoDeEmbalagem) !== Number(idTipoEmbalagem)
      )
    );
  };

  return (
    <Overlay>
      <Modal>
        <Title>Editar Fornecedor</Title>

        <InputGroup>
          <Input
            value={data.fornecedorDescricao}
            disabled
            onChange={(e) =>
              setData((prev) => ({
                ...prev,
                fornecedorDescricao: e.target.value,
              }))
            }
          />
          <Label>Nome</Label>
        </InputGroup>

        <InputGroup>
          <Input
            value={data.cpfCnpj || ""}
            disabled
            maxLength={14}
            onChange={(e) =>
              setData((prev) => ({ ...prev, cpfCnpj: e.target.value }))
            }
          />
          <Label>CPF/CNPJ</Label>
        </InputGroup>

        {/* Unidades */}
        <SubTitle>Unidades</SubTitle>
        <InputGroup>
          <Input
            value={novaUnidade}
            disabled={loading}
            onChange={(e) => setNovaUnidade(e.target.value)}
            placeholder="Nova unidade"
          />
          <AddButton type="button" onClick={handleAddUnidade}>
            Adicionar
          </AddButton>
        </InputGroup>

        <UnidadesList>
          {data.unidades
            .filter((unit) => unit.deleted !== 1)
            .map((unit, idx) => (
              <UnidadeItem key={unit.idFornecedorUnidade ?? idx}>
                {unit.fornecedorUnidadeDescricao}
                <RemoveButton onClick={() => handleRemoverUnidade(unit)}>
                  <AiFillDelete size={14} />
                </RemoveButton>
              </UnidadeItem>
            ))}
        </UnidadesList>

        {/* Tipos de Embalagens */}
        <SubTitle>Tipos de Embalagens</SubTitle>
        <SelectWrapper>
          <SelectedEmbalagens>
            {tipoEmbalagensVinculadas.map((emb) => (
              <EmbalagemTag key={emb.idTipoDeEmbalagem}>
                {emb.tipoEmbalagem || "Sem nome"}
                <RemoveButton
                  onClick={() => handleRemoverEmbalagem(emb.idTipoDeEmbalagem)}
                >
                  <AiFillDelete size={16} />
                </RemoveButton>
              </EmbalagemTag>
            ))}
          </SelectedEmbalagens>

          <SelectToggle onClick={() => setShowSelect(!showSelect)}>
            <MdArrowDropDown size={24} />
          </SelectToggle>

          {showSelect && (
            <OptionsBox>
              {tipoEmbalagensParaAdicionar.map((add) => (
                <OptionItem
                  key={add.idTipoEmbalagem}
                  onClick={() => handleToggleEmbalagem(add)}
                >
                  {add.tipoEmbalagem}
                </OptionItem>
              ))}
            </OptionsBox>
          )}
        </SelectWrapper>

        <ButtonRow>
          <CancelButton onClick={onCancel}>Voltar</CancelButton>
        </ButtonRow>
      </Modal>
    </Overlay>
  );
};

export default EditarFornecedor;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Modal = styled.div`
  background: white;
  width: 500px;
  max-height: 90vh; /* Limita altura do modal na tela */
  overflow-y: auto; /* Habilita rolagem interna */
  padding: 30px;
  border-radius: 10px;
  position: relative;
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 20px;
`;

const SubTitle = styled.h3`
  font-size: 16px;
  margin: 20px 0 10px;
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1.8px solid #cfd8dc;
  border-radius: 6px;
  font-size: 16px;
`;

const Label = styled.label`
  position: absolute;
  top: -8px;
  left: 12px;
  background: white;
  padding: 0 6px;
  font-size: 13px;
  color: #607d8b;
`;

const AddButton = styled.button`
  margin-top: 8px;
  padding: 8px 12px;
  background: #3f51b5;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
`;

const UnidadesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 150px;
  overflow-y: auto;
  padding-right: 6px; /* Para evitar cortar conteúdo com scrollbar */
`;

const UnidadeItem = styled.div`
  background: #e0e0e0;
  padding: 6px 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const SelectedEmbalagens = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 150px;
  overflow-y: auto;
  padding-right: 6px;
  margin-top: 8px;
`;

const EmbalagemTag = styled.span`
  background: #e8e8e8;
  padding: 5px 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
`;

const RemoveButton = styled.button`
  margin-left: 8px;
  background: none;
  border: none;
  color: red;
  cursor: pointer;
`;

const SelectToggle = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
`;

const OptionsBox = styled.div`
  position: absolute;
  top: 35px;
  left: 0;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 4px;
  z-index: 10;
`;

const OptionItem = styled.button`
  width: 100%;
  padding: 10px;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #ddd;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
`;

const SaveButton = styled.button`
  padding: 12px 20px;
  background: #83620e;
  color: white;
  font-weight: bold;
  border: none;
  border-radius: 6px;
`;

const CancelButton = styled(SaveButton)`
  width: 100%;
  background: #e0e0e0;
  color: #333;
`;
